(() => {
  if (window.EssentiaDelivery) return;
  const node = document.getElementById('essentia-delivery-config');
  if (!node) return;
  const config = JSON.parse(node.textContent);
  const text = (key, values = {}) => Object.entries(values).reduce((result, [name, value]) => result.replaceAll(`{${name}}`, value), config.labels[key]);
  const format = new Intl.DateTimeFormat('en-CA', { timeZone: config.timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
  const timelineDate = new Intl.DateTimeFormat('es-PY', { timeZone: 'UTC', day: 'numeric', month: 'short' });
  const weekdays = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
  const closedDates = new Set((config.closedDates || '').split(/[\s,;]+/).filter(Boolean));
  const dayMs = 86400000;
  function schedule(epoch) {
    const p = Object.fromEntries(format.formatToParts(new Date(epoch)).map(part => [part.type, part.value]));
    const date = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day));
    const day = new Date(date).getUTCDay();
    const key = value => new Date(value).toISOString().slice(0, 10);
    const seconds = Number(p.hour) * 3600 + Number(p.minute) * 60 + Number(p.second) + (epoch % 1000) / 1000;
    const cutoff = Number(day === 6 ? config.saturdayHour : config.weekdayHour) * 3600;
    const isClosed = value => new Date(value).getUTCDay() === 0 || closedDates.has(key(value));
    const open = !config.suspended && !isClosed(date) && seconds < cutoff;
    let next = date;
    if (!open) {
      next += dayMs;
      for (let tries = 0; isClosed(next) && tries < 730; tries++) next += dayMs;
    }
    const daysAway = Math.round((next - date) / dayMs);
    return { date, next, open, remaining: open ? Math.max(0, cutoff - seconds) : 0, percent: open ? Math.max(0, Math.min(100, (cutoff - seconds) / cutoff * 100)) : 0, cutoff: `${String(cutoff / 3600).padStart(2, '0')}:00`, day, nextDay: weekdays[new Date(next).getUTCDay()], daysAway, special: closedDates.has(key(date)), suspended: !!config.suspended };
  }
  const components = new Set();
  let anchor = null;
  let syncing = null;
  let timer = null;
  let lastAttempt = -Infinity;
  const current = () => anchor && anchor.epoch + performance.now() - anchor.monotonic;
  function write(el, selector, value) {
    const target = el.querySelector(selector);
    if (target && target.textContent !== value) target.textContent = value;
  }
  function render(el) {
    const epoch = current();
    const valid = epoch !== null && performance.now() - anchor.monotonic < 360000;
    const s = valid ? schedule(epoch) : null;
    const zone = el.dataset.deliveryZone || 'unknown';
    let title = text('generic_title');
    let detail = text('generic_detail');
    let note = config.note;
    let state = 'neutral';
    let percent = 0;
    if (zone === 'interior') {
      title = text('interior_title');
      detail = config.interior;
      note = '';
    } else if (s?.suspended) {
      title = text('suspended_title');
      detail = text('suspended_detail');
    } else if (s?.open) {
      const seconds = Math.ceil(s.remaining);
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor(seconds / 60) % 60;
      const countdown = `${String(hours).padStart(2, '0')} h ${String(minutes).padStart(2, '0')} min ${String(seconds % 60).padStart(2, '0')} s`;
      title = text('today');
      detail = text('countdown', { cutoff: s.cutoff, countdown });
      state = s.remaining <= 7200 ? 'urgent' : 'open';
      percent = s.percent;
    } else if (s) {
      const target = s.daysAway === 1 ? text('tomorrow') : text('on_day', { day: s.nextDay.toUpperCase() });
      title = text('next', { target });
      detail = s.day === 0 ? text('sunday') : s.special ? text('special') : text('closed', { day: s.nextDay });
    }
    const timeline = el.querySelector('[data-delivery-timeline]');
    if (timeline) {
      timeline.hidden = !s || s.suspended || zone === 'interior';
      if (!timeline.hidden) {
        write(el, '[data-delivery-order]', timelineDate.format(s.date));
        write(el, '[data-delivery-prep]', timelineDate.format(s.next));
        write(el, '[data-delivery-arrival]', timelineDate.format(s.next));
      }
    }
    el.dataset.state = state;
    write(el, '[data-delivery-title]', title);
    write(el, '[data-delivery-detail]', detail);
    write(el, '[data-delivery-note]', note ?? text('fallback_note'));
    const track = el.querySelector('[role=progressbar]');
    if (track) {
      track.setAttribute('aria-valuenow', String(Math.round(percent)));
      track.setAttribute('aria-valuetext', detail);
    }
    const fill = el.querySelector('[data-delivery-fill]');
    if (fill) fill.style.width = `${percent.toFixed(3)}%`;
  }
  function update() { components.forEach(render); }
  async function sync(force = false) {
    if (syncing) return syncing;
    if (force) { anchor = null; update(); }
    lastAttempt = performance.now();
    syncing = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 7000);
      try {
        const url = new URL(config.clockUrl, location.origin);
        url.searchParams.set('_essentia_clock', `${Math.random().toString(36).slice(2)}`);
        const start = performance.now();
        const response = await fetch(url, { method: 'HEAD', cache: 'no-store', credentials: 'same-origin', signal: controller.signal });
        const end = performance.now();
        const epoch = Date.parse(response.headers.get('date') || '');
        const age = Number(response.headers.get('age') || 0);
        if (response.ok && Number.isFinite(epoch) && age <= 2 && end - start < 7000) {
          anchor = { epoch: epoch + (end - start) + 1000 + age * 1000, monotonic: end };
        } else { anchor = null; }
      } catch { anchor = null; }
      finally { clearTimeout(timeout); update(); syncing = null; }
    })();
    return syncing;
  }
  class DeliveryBar extends HTMLElement {
    connectedCallback() {
      components.add(this);
      if (!this.querySelector('[data-delivery-title]')) return;
      render(this);
      if (!timer) timer = setInterval(() => {
        if (document.hidden) return;
        update();
        if (performance.now() - lastAttempt > 30000 && (!anchor || performance.now() - anchor.monotonic > 300000)) sync();
      }, 1000);
      if (!anchor) sync();
    }
    disconnectedCallback() {
      components.delete(this);
      if (!components.size) { clearInterval(timer); timer = null; }
    }
  }
  window.EssentiaDelivery = { schedule, sync };
  customElements.define('essentia-delivery', DeliveryBar);
  document.addEventListener('visibilitychange', () => { if (!document.hidden && components.size) sync(true); });
  window.addEventListener('pageshow', event => { if (event.persisted && components.size) sync(true); });
  window.addEventListener('online', () => { if (components.size) sync(true); });
})();
